import {
    BehaviorSubject,
    combineLatest,
    map,
    Observable,
    shareReplay,
    Subscription
} from 'rxjs';

import { Injectable } from '@angular/core';

/**
 * The Work Tracker simply keeps track of the number of outstanding tokens it has
 * issued as a way of tracking how many tasks are currently running.
 *
 * It can be used directly, or as a scoped service.
 */
@Injectable()
export class WorkTracker {
    private counter = 0;
    private outstanding: number[] = [];
    private subject = new BehaviorSubject<boolean>(false);

    private parentTracker: WorkTracker;
    private parentTrackerSubscription: Subscription;

    private parentSubject = new BehaviorSubject<boolean>(false);

    observable = combineLatest([this.subject, this.parentSubject]).pipe(
        map(([a, b]) => a || b),
        shareReplay()
    );

    ngOnDestroy() {
        this.subject.complete();
        this.parentSubject.complete();
        this.parentTrackerSubscription?.unsubscribe();
    }

    /**
     * Get the next token.
     */
    private next() {
        const nextCounter = this.counter++;
        this.outstanding.push(nextCounter);

        return nextCounter;
    }

    /**
     * Create a tracker, and return a function that is used to close off the task.
     */
    createTracker(): () => void {
        if (this.parentTracker) {
            return this.parentTracker.createTracker();
        }

        const counter = this.next();

        this.subject.next(true);

        return () => this.destroyToken(counter);
    }

    private destroyToken(token: number) {
        this.outstanding.splice(this.outstanding.indexOf(token), 1);

        if (this.outstanding.length === 0) {
            this.subject.next(false);
        }
    }

    active(): boolean {
        return this.subject.getValue() || this.parentSubject.getValue();
    }

    /**
     * @deprecated use `observable` instead
     */
    asObservable() {
        return this.observable;
    }

    private cleanup() {
        // Clean up old parent tracker
        if (this.parentTrackerSubscription != null) {
            this.parentTrackerSubscription.unsubscribe();
            this.parentTrackerSubscription = null;
            this.parentTracker = null;
        }
    }

    setParentWorkingObservable(parentObservable: Observable<boolean>) {
        this.cleanup();

        if (parentObservable != null) {
            this.parentTrackerSubscription = parentObservable.subscribe(
                working => this.parentSubject.next(working)
            );
        } else {
            this.parentSubject.next(false);
        }
    }

    setParentTracker(parentTracker: WorkTracker) {
        this.cleanup();

        // Setup new parent tracker if required
        if (parentTracker != null) {
            this.parentTracker = parentTracker;
            this.parentTrackerSubscription =
                this.parentTracker.observable.subscribe(working =>
                    this.parentSubject.next(working)
                );
            this.parentSubject.next(parentTracker.active());
        } else {
            this.parentSubject.next(false);
        }
    }
}
